import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import Page from '../../components/page/Page'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  color: '#fff',
  padding: theme.spacing(8, 2),
  textAlign: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 50% 50%, rgba(255, 193, 7, 0.1) 0%, transparent 50%)',
  },
}))

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}))

const ProductImage = styled(CardMedia)({
  paddingTop: '100%',
  backgroundColor: '#f5f5f5',
})

const FeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}))

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    color: '#ffc107',
  },
}))

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffc107',
  color: '#000',
  padding: theme.spacing(1.5, 4),
  fontSize: '1.1rem',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#ffb300',
  },
}))

const products = [
  {
    id: 1,
    name: 'Rain Jackets',
    description:
      'Weather-resistant performance jackets with the signature HYVE branding. Built for all conditions.',
    image: '/images/clothing/rain-jacket.webp',
    category: 'Outerwear',
  },
  {
    id: 2,
    name: 'Performance T-Shirts',
    description:
      'Premium performance tees designed for high-intensity training. Breathable and comfortable.',
    image: '/images/clothing/tshirt.webp',
    category: 'Tops',
  },
  {
    id: 3,
    name: 'Training Vests',
    description:
      'Lightweight training vests for maximum mobility. Perfect for your toughest workouts.',
    image: '/images/clothing/vest.webp',
    category: 'Tops',
  },
]

const Clothing = () => {
  const handleShopNow = () => {
    window.open(
      'https://www.propanther.co.uk/hyvegym',
      '_blank',
      'noopener,noreferrer'
    )
  }

  return (
    <Page hideFooter={false} container={false}>
      <HeroSection>
        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant='h4'
            component='h2'
            gutterBottom
            sx={{ fontWeight: 500, mt: 2 }}
          >
            The Hyve Gym × Pro Panther Collab
          </Typography>
          <Typography
            variant='h6'
            sx={{ mt: 3, mb: 4, opacity: 0.9, maxWidth: '800px', mx: 'auto' }}
          >
            Built for performance. Styled for everyday wear.
          </Typography>
          <CTAButton variant='contained' size='large' onClick={handleShopNow}>
            Shop Now
          </CTAButton>
        </Container>
      </HeroSection>

      <Container maxWidth='lg' sx={{ py: 8 }}>
        <Typography
          variant='h3'
          component='h2'
          align='center'
          gutterBottom
          sx={{ fontWeight: 600, mb: 2 }}
        >
          Performance Clothing Collection
        </Typography>
        <Typography
          variant='body1'
          align='center'
          color='text.secondary'
          sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
        >
          Discover our exclusive range of high-performance athletic wear,
          designed in collaboration with Pro Panther.
        </Typography>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <ProductCard>
                <ProductImage image={product.image} title={product.name} />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant='overline'
                    color='primary'
                    sx={{ fontWeight: 600 }}
                  >
                    {product.category}
                  </Typography>
                  <Typography
                    variant='h5'
                    component='h3'
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {product.name}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {product.description}
                  </Typography>
                </CardContent>
              </ProductCard>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ backgroundColor: '#f9f9f9', borderRadius: 2, p: 6, mb: 6 }}>
          <Typography
            variant='h4'
            component='h2'
            align='center'
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            Why Choose Hyve Performance Wear?
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FeatureList>
                <FeatureItem>
                  <CheckCircleIcon />
                  <Typography variant='body1' color={'black'}>
                    <strong>Order directly</strong> from the Pro Panther website
                  </Typography>
                </FeatureItem>
                <FeatureItem>
                  <CheckCircleIcon />
                  <Typography variant='body1' color={'black'}>
                    <strong>Delivered straight to your door</strong> OR collect
                    from Hyve Gym, Llanelli
                  </Typography>
                </FeatureItem>
              </FeatureList>
            </Grid>
            <Grid item xs={12} md={6}>
              <FeatureList>
                <FeatureItem>
                  <CheckCircleIcon />
                  <Typography variant='body1' color={'black'}>
                    <strong>FREE customisation</strong> with your initials
                  </Typography>
                </FeatureItem>
                <FeatureItem>
                  <CheckCircleIcon />
                  <Typography variant='body1' color={'black'}>
                    <strong>Premium quality</strong> built for performance
                  </Typography>
                </FeatureItem>
              </FeatureList>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant='h5' gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Ready to elevate your training?
          </Typography>
          <CTAButton variant='contained' size='large' onClick={handleShopNow}>
            Shop the Collection
          </CTAButton>
        </Box>
      </Container>
    </Page>
  )
}

export default Clothing
